/**
 * Boot-time post-login navigation for the root page (`/`).
 *
 * Extracted from `src/pages/index.tsx` so the MOD-1482 boot-routing fix
 * (the hard-navigation fallback and the desktop-token redirect gate) can
 * carry a regression test without turning the page file into a route-emitting
 * test host (`src/pages/*` has no `pageExtensions` filter, so a co-located
 * page test would ship as a static-export route).
 */
import type { NextRouter } from 'next/router';

import { logger } from '@/utils/logger/logger';

const HARD_NAVIGATION_FALLBACK_MS = 3000;

export type BootNavigator = {
  navigateAfterLogin: (redirectTarget: string) => Promise<void>;
  /** Effect cleanup: disarms the pending hard-navigation fallback timer. */
  cleanup: () => void;
};

/**
 * Create ONE navigator per page mount and reuse it across effect re-runs.
 *
 * The redirect effect re-runs several times during a desktop login —
 * `authProvider`, the Redux token and the context session land in separate
 * renders, and `useKinde()`'s Tauri branch returns a fresh `getToken` per
 * render — and every re-run used to build a new navigator and call
 * `router.replace` again. Next cancels the in-flight route change when a
 * second one starts, so the first call resolved `false` and hard-navigated
 * immediately, turning a client-side route change into a full webview reload
 * (DSK-936). The navigator therefore owns the in-flight state itself and
 * takes the target per call, so the page can memoise it on the router.
 */
export function createBootNavigator({
  router,
  hardNavigationDelayMs = HARD_NAVIGATION_FALLBACK_MS,
  beforeHardNavigation,
}: {
  router: Pick<NextRouter, 'replace'>;
  hardNavigationDelayMs?: number;
  /**
   * Runs (and is awaited) right before a hard `window.location.assign`. The
   * page passes the redux-persist flush here: a reload that beats the
   * throttled `persist:root` write rehydrates the token without
   * `authProvider`, and TauriWrapper then reads the fresh Auth0 desktop
   * session as a stale Kinde token and wipes it (DSK-936). Failures are
   * logged and never block the navigation.
   */
  beforeHardNavigation?: () => Promise<unknown>;
}): BootNavigator {
  let hardNavigationTimer: ReturnType<typeof setTimeout> | undefined;
  let inFlight = false;
  // Target of the route change currently in flight. A re-run with the SAME
  // target is a duplicate and is skipped; a re-run with a DIFFERENT target
  // (e.g. `post_login_redirect_url` hydrating after the first attempt) must
  // supersede it — Next cancels the older change, which the cancelled branch
  // below now treats as benign.
  let pendingTarget: string | undefined;
  let hardNavigated = false;
  // Absolute deadline of the current attempt. Re-arming after an effect
  // re-run keeps THIS deadline rather than restarting the clock, otherwise a
  // page that re-renders faster than `hardNavigationDelayMs` (the Tauri
  // `getToken` is a fresh arrow per render) would push the backstop out
  // forever while `router.replace` hangs.
  let deadlineAt: number | undefined;

  const hardNavigate = async (redirectTarget: string, reason: string) => {
    // One hard navigation per page, ever: the effect keeps re-running while
    // the webview tears the page down, and a second `assign` is the reload
    // storm this fix exists to remove.
    if (hardNavigated) return;
    hardNavigated = true;
    logger.warn({ redirectTarget }, `[index] ${reason}; forcing hard navigation`);
    if (beforeHardNavigation) {
      try {
        await beforeHardNavigation();
      } catch (error) {
        logger.warn(
          { error, redirectTarget },
          '[index] beforeHardNavigation failed; continuing with hard navigation'
        );
      }
    }
    // The awaited flush is a window (up to ~1s) in which a client-side route
    // change can complete; a hard `assign` after it would be the very reload
    // this module exists to avoid. `hardNavigated` stays set: the transition
    // landed, so no later hard navigation is wanted either.
    if (window.location.pathname !== '/') {
      logger.warn(
        { redirectTarget, pathname: window.location.pathname },
        '[index] Route left / during the pre-navigation flush; skipping hard navigation'
      );
      return;
    }
    window.location.assign(redirectTarget);
  };

  const armFallbackTimer = (redirectTarget: string) => {
    if (hardNavigationTimer) clearTimeout(hardNavigationTimer);
    if (deadlineAt === undefined) deadlineAt = Date.now() + hardNavigationDelayMs;
    hardNavigationTimer = setTimeout(
      () => {
        if (window.location.pathname === '/') {
          void hardNavigate(redirectTarget, 'Boot navigation made no progress');
        }
      },
      Math.max(0, deadlineAt - Date.now())
    );
  };

  const navigateAfterLogin = async (redirectTarget: string) => {
    // Bounded backstop: only ever fires while the page is still on `/`, so it
    // is harmless to re-arm on every effect run — and it MUST be, because the
    // previous run's `cleanup` disarmed it. The deadline itself is not moved.
    armFallbackTimer(redirectTarget);

    if (inFlight && pendingTarget === redirectTarget) {
      logger.debug(
        { redirectTarget },
        '[index] Boot navigation already in flight; not starting another route change'
      );
      return;
    }
    inFlight = true;
    pendingTarget = redirectTarget;

    try {
      const didNavigate = await router.replace(redirectTarget);
      if (didNavigate) {
        // Settled client-side: the backstop has nothing left to guard.
        if (hardNavigationTimer) clearTimeout(hardNavigationTimer);
        deadlineAt = undefined;
        inFlight = false;
        pendingTarget = undefined;
        return;
      }
      // Cancelled means a newer route change superseded this one. Do NOT hard
      // navigate here: the superseding change is what the user is about to
      // see, and a hard `assign` on top of it reloads the webview mid-login.
      // The fallback timer still hard-navigates if the page is on `/` after
      // the bound elapses.
      logger.warn(
        { redirectTarget },
        '[index] Boot route replacement was cancelled; leaving the bounded fallback timer armed'
      );
    } catch (error) {
      logger.warn(
        { error, redirectTarget },
        '[index] Boot route replacement failed; falling back to hard navigation'
      );
      if (hardNavigationTimer) clearTimeout(hardNavigationTimer);
      await hardNavigate(redirectTarget, 'Boot route replacement failed');
    } finally {
      // Only the call that still owns the flight releases it — a superseded
      // (cancelled) call must not clear the state of the one that replaced it.
      // `deadlineAt` deliberately survives a cancelled replace: the page is
      // still on `/`, and the next effect re-run must inherit the original
      // deadline rather than restart the clock.
      if (pendingTarget === redirectTarget) {
        inFlight = false;
        pendingTarget = undefined;
      }
    }
  };

  const cleanup = () => {
    if (hardNavigationTimer) clearTimeout(hardNavigationTimer);
  };

  return { navigateAfterLogin, cleanup };
}

/**
 * Bound a redux-persist flush so a stalled storage write can never hold the
 * boot loader: resolves when the flush settles or `timeoutMs` elapses,
 * whichever is first. A throwing/rejecting flush resolves too — the flush is
 * best-effort, the navigation is not.
 */
export function boundedPersistFlush(
  flush: () => Promise<unknown>,
  timeoutMs: number
): Promise<void> {
  return new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, timeoutMs);
    let flushed: Promise<unknown>;
    try {
      flushed = flush();
    } catch {
      flushed = Promise.resolve();
    }
    void flushed
      .catch(() => undefined)
      .then(() => {
        clearTimeout(timer);
        resolve();
      });
  });
}

/**
 * The desktop boot redirect gate: should the root page treat this session as
 * an authenticated desktop-token provider and navigate into the app?
 */
export function shouldRedirectDesktopTokenProvider({
  isAuth0Desktop,
  isKeycloak,
  token,
  desktopAccessToken,
}: {
  isAuth0Desktop: boolean;
  isKeycloak: boolean;
  token: string | null | undefined;
  desktopAccessToken: string | null;
}): boolean {
  if (isKeycloak && Boolean(token)) return true;
  return isAuth0Desktop && Boolean(token || desktopAccessToken);
}
